/**
 * Fire Marker Icon Component
 *
 * Renders fire severity markers as React SVG components instead of using
 * dangerouslySetInnerHTML. This eliminates XSS risk from SVG injection.
 *
 * Marker types:
 * - Diamond (white): NOT_APPLICABLE alert level
 * - Triangle (yellow): ADVICE
 * - Triangle (orange): WATCH_AND_ACT
 * - Triangle (dark red): EMERGENCY
 */

import { memo } from 'react';

export type FireSeverity = 'NOT_APPLICABLE' | 'ADVICE' | 'WATCH_AND_ACT' | 'EMERGENCY' | string;

interface FireMarkerIconProps {
  severity: FireSeverity;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  style?: React.CSSProperties;
  /**
   * When true, the marker border is rendered as a dashed/dotted stroke
   * to distinguish planned burns / hazard reductions from wildfires —
   * matches the convention used by Hazards Near Me. Severity colour and
   * marker shape are unchanged so users can still triage at a glance.
   */
  isPlannedBurn?: boolean;
}

// Flame SVG path - reusable across all variants
const FlamePath = memo(({ fill }: { fill: string }) => (
  <path
    d="M12 4C12 4 5 10 5 14.5C5 18 7.5 20 10.5 20C10.5 20 8 18 8 15.5C8 13 10 11 10 11C10 11 9 14 11 16C11 16 10 13 12 10C14 13 13 16 13 16C15 14 14 11 14 11C14 11 16 13 16 15.5C16 18 13.5 20 13.5 20C16.5 20 19 18 19 14.5C19 10 12 4 12 4Z"
    fill={fill}
  />
));
FlamePath.displayName = 'FlamePath';

// Size configurations
const SIZE_CONFIG = {
  small: { diamond: 24, triangle: { width: 20, height: 18 } },
  medium: { diamond: 32, triangle: { width: 40, height: 36 } },
  large: { diamond: 40, triangle: { width: 48, height: 44 } },
};

/**
 * Diamond marker for NOT_APPLICABLE severity
 */
const DiamondMarker = memo(({ size, dashed, style }: { size: number; dashed?: boolean; style?: React.CSSProperties }) => (
  <svg
    viewBox="0 0 32 32"
    width={size}
    height={size}
    style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))', ...style }}
    aria-label={dashed ? 'Planned burn - Not Applicable severity' : 'Fire incident - Not Applicable severity'}
  >
    <rect
      x="4"
      y="4"
      width="24"
      height="24"
      transform="rotate(45 16 16)"
      fill="white"
      stroke="black"
      strokeWidth="2"
      strokeDasharray={dashed ? '3,2' : undefined}
    />
    {/* Center flame in diamond: scale 0.85, translate to center at (16,16) */}
    <g transform="translate(6, 4) scale(0.85)">
      <FlamePath fill="black" />
    </g>
  </svg>
));
DiamondMarker.displayName = 'DiamondMarker';

/**
 * Triangle marker for ADVICE, WATCH_AND_ACT, and EMERGENCY severities
 */
const TriangleMarker = memo(({
  triangleColor,
  flameColor,
  width,
  height,
  style,
  ariaLabel,
  dashed,
}: {
  triangleColor: string;
  flameColor: string;
  width: number;
  height: number;
  style?: React.CSSProperties;
  ariaLabel: string;
  dashed?: boolean;
}) => (
  <svg
    viewBox="0 0 40 36"
    width={width}
    height={height}
    style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))', ...style }}
    aria-label={ariaLabel}
  >
    <path
      d="M20 2 L38 34 L2 34 Z"
      fill={triangleColor}
      stroke="black"
      strokeWidth="2.5"
      strokeDasharray={dashed ? '4,2.5' : undefined}
    />
    <g transform="translate(10, 10) scale(0.8)">
      <FlamePath fill={flameColor} />
    </g>
  </svg>
));
TriangleMarker.displayName = 'TriangleMarker';

/**
 * Get severity-specific colors
 */
function getSeverityColors(severity: FireSeverity): { triangleColor: string; flameColor: string } {
  switch (severity) {
    case 'EMERGENCY':
      return { triangleColor: '#b91c1c', flameColor: 'white' };
    case 'WATCH_AND_ACT':
      return { triangleColor: '#f97316', flameColor: 'black' };
    case 'ADVICE':
    default:
      return { triangleColor: '#fbbf24', flameColor: 'black' };
  }
}

/**
 * Get severity aria label
 */
function getSeverityLabel(severity: FireSeverity): string {
  switch (severity) {
    case 'EMERGENCY':
      return 'Fire incident - Emergency Warning';
    case 'WATCH_AND_ACT':
      return 'Fire incident - Watch and Act';
    case 'ADVICE':
      return 'Fire incident - Advice';
    case 'NOT_APPLICABLE':
      return 'Fire incident - Not Applicable';
    default:
      return 'Fire incident';
  }
}

/**
 * Main Fire Marker Icon Component
 */
const FireMarkerIcon = memo(({
  severity,
  size = 'medium',
  className,
  style,
  isPlannedBurn,
}: FireMarkerIconProps) => {
  const sizeConfig = SIZE_CONFIG[size];

  if (severity === 'NOT_APPLICABLE') {
    return (
      <span className={className} style={{ display: 'inline-flex', ...style }}>
        <DiamondMarker size={sizeConfig.diamond} dashed={isPlannedBurn} />
      </span>
    );
  }

  const { triangleColor, flameColor } = getSeverityColors(severity);
  const { width, height } = sizeConfig.triangle;
  const baseLabel = getSeverityLabel(severity);
  const ariaLabel = isPlannedBurn ? `Planned burn — ${baseLabel}` : baseLabel;

  return (
    <span className={className} style={{ display: 'inline-flex', ...style }}>
      <TriangleMarker
        triangleColor={triangleColor}
        flameColor={flameColor}
        width={width}
        height={height}
        ariaLabel={ariaLabel}
        dashed={isPlannedBurn}
      />
    </span>
  );
});
FireMarkerIcon.displayName = 'FireMarkerIcon';

/**
 * Fire Group Header Icon - Triangle with fire emoji for list headers
 */
interface FireGroupHeaderIconProps {
  className?: string;
  style?: React.CSSProperties;
}

const FireGroupHeaderIcon = memo(({ className, style }: FireGroupHeaderIconProps) => (
  <span className={className} style={{ display: 'inline-flex', alignItems: 'center', ...style }}>
    <svg
      viewBox="0 0 40 36"
      width={20}
      height={18}
      style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}
      aria-label="Fire Incidents"
    >
      <path
        d="M20 2 L38 34 L2 34 Z"
        fill="#dc2626"
        stroke="black"
        strokeWidth="2.5"
      />
      {/* P1-1: previously this rendered the 🔥 emoji inside an SVG <text>
          element. Not all Android system WebViews can render emoji inside
          SVG text — some fall back to a tofu box at marker scale, breaking
          the list-group header visual. Use the existing FlamePath SVG
          instead. The translate centres the 24×24 flame inside the 40×36
          triangle (apex y=2, base y=34 → centre ≈ y=23). */}
      <g transform="translate(8 11)">
        <FlamePath fill="#fff" />
      </g>
    </svg>
  </span>
));
FireGroupHeaderIcon.displayName = 'FireGroupHeaderIcon';

export default FireMarkerIcon;

// Export individual components for flexibility
export { DiamondMarker, TriangleMarker, FlamePath, FireGroupHeaderIcon };
